import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";

import { exploreSurveyIntentPath } from "../../configs/ExploreSurveyConfig";
import {
  DEFAULT_DASHBOARD_TIME_RANGE,
  DEFAULT_UNAVAILABLE_TIME_RANGES,
  coerceDashboardTimeRange,
  getDashboardDateRange,
} from "../../services/helpers/DashboardHelpers";
import { createLogger } from "../../services/Logger";
import { pageConfigBySlug } from "../../services/nav/NavigationService";
import {
  INextGenDashboardChart,
  INextGenDashboardKpi,
  INextGenDashboardQuestion,
  INextGenDashboardResponse,
} from "../../services/zeos-api/contracts/Dashboard";
import { IZeosApiResponse } from "../../services/zeos-api/contracts/ZeosApi";
import { ZeosApiService } from "../../services/zeos-api/ZeosApiService";
import { useDashboardConnectCta } from "../Dashboard/shared/DashboardConnectProvider";
import {
  DashboardTileKey,
  getDashboardTileStatusCopy,
  hasDashboardConnectCta,
  IDashboardTileStatusCopy,
} from "../Dashboard/shared/dashboardTileStatusCopy";
import { DashboardStatusBadge } from "../Dashboard/shared/DashboardStatusBadge";
import { DashboardStatusBanner } from "../Dashboard/shared/DashboardStatusBanner";
import { DashboardExceptionPanel } from "../Dashboard/shared/DashboardExceptionPanel";
import { ChartCard } from "./ChartCard";
import { DataStatusStrip } from "./DataStatusStrip";
import { DetailFilterBar } from "./DetailFilterBar";
import { FilterPropertySelect } from "./FilterPropertySelect";
import { CoverageDisclosure } from "../Dashboard/shared/CoverageDisclosure";
import { KpiStrip } from "./KpiStrip";
import { NextGenChart } from "./NextGenChart";
import { NextGenChartControls } from "./NextGenChartControls";
import { NextGenHeatmap } from "./NextGenHeatmap";

interface IProps {
  propertyId?: number;
  /** Lets the coverage chip open the meters panel for people who can manage meters (task-4051). */
  companyId?: number;
  initialTileId?: string;
  onFailedLoad?: () => void;
}

const DEFAULT_TILE = "building";

// DB-R6: avoids refetching a tile/range this session has already loaded recently
// (e.g. toggling 1M -> 3M -> 1M, or navigating back to a tile category).
const SERIES_CACHE_TTL_MS = 60_000;
const seriesCache = new Map<
  string,
  { response: IZeosApiResponse<INextGenDashboardResponse>; fetchedAt: number }
>();

/** Drop cached series for a property so a fresh connect isn't masked by a stale cache hit. */
export const invalidateNextGenSeriesCache = (propertyId: number) => {
  const prefix = `${propertyId}:`;
  for (const key of seriesCache.keys()) {
    if (key.startsWith(prefix)) {
      seriesCache.delete(key);
    }
  }
};

const toDashboardTileKey = (id: string): DashboardTileKey => {
  if (
    id === "building" ||
    id === "solar" ||
    id === "ev" ||
    id === "carbon" ||
    id === "battery" ||
    id === "dr"
  ) {
    return id;
  }
  return "building";
};

export const NextGenDashboard = ({
  propertyId,
  companyId,
  initialTileId,
  onFailedLoad,
}: IProps) => {
  const log = useMemo(() => createLogger("NextGenDashboard"), []);
  const tileId = initialTileId ?? DEFAULT_TILE;
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<string>(
    DEFAULT_DASHBOARD_TIME_RANGE
  );
  const [unavailableTimeRanges, setUnavailableTimeRanges] = useState<string[]>(
    DEFAULT_UNAVAILABLE_TIME_RANGES
  );
  const unavailableTimeRangesRef = React.useRef(unavailableTimeRanges);
  unavailableTimeRangesRef.current = unavailableTimeRanges;
  const [compareMode, setCompareMode] = useState<string>("PriorPeriod");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [statusBanner, setStatusBanner] = useState<IDashboardTileStatusCopy | null>(null);
  const [partialCopy, setPartialCopy] = useState<IDashboardTileStatusCopy | null>(
    null
  );
  const [data, setData] = useState<INextGenDashboardResponse | null>(null);
  const [hasFailed, setHasFailed] = useState(false);
  const connect = useDashboardConnectCta();

  useEffect(() => {
    unavailableTimeRangesRef.current = DEFAULT_UNAVAILABLE_TIME_RANGES;
    setUnavailableTimeRanges(DEFAULT_UNAVAILABLE_TIME_RANGES);
  }, [propertyId]);

  const fetchData = useCallback(async () => {
    if (!propertyId) {
      return;
    }

    setLoading(true);
    setError(null);
    setStatusBanner(null);
    setPartialCopy(null);
    setHasFailed(false);

    try {
      const effectiveRange = coerceDashboardTimeRange(
        timeRange,
        unavailableTimeRangesRef.current
      );
      if (effectiveRange !== timeRange) {
        setTimeRange(effectiveRange);
        return;
      }

      const { startDate, endDate } = getDashboardDateRange(effectiveRange);
      const started = performance.now();
      // compareMode is part of the key so switching basis (PriorPeriod <-> YoY) refetches
      // instead of serving the other basis's cached response.
      const cacheKey = `${propertyId}:${tileId}:${effectiveRange}:${compareMode}`;
      const cached = seriesCache.get(cacheKey);
      const isCacheHit =
        !!cached && Date.now() - cached.fetchedAt < SERIES_CACHE_TTL_MS;
      const res = isCacheHit
        ? cached.response
        : await ZeosApiService.Dashboard.getNextGenDashboard({
            propertyId,
            tileId,
            timeRange: effectiveRange,
            startDate,
            endDate,
            compareMode,
          });
      log.info("Dashboard NextGen loaded", {
        propertyId,
        tileId,
        timeRange: effectiveRange,
        startDate,
        endDate,
        elapsedMs: Math.round(performance.now() - started),
        cached: isCacheHit,
        status: res.data?.status ?? res.zeosStatus,
      });

      if (res.zeosStatus?.toLowerCase() !== "success" || !res.data) {
        const copy = getDashboardTileStatusCopy(
          toDashboardTileKey(tileId),
          "error",
          res.message
        );
        setStatusBanner(copy);
        setError(copy.body);
        setHasFailed(true);
        setData(null);
        return;
      }

      if (!isCacheHit) {
        seriesCache.set(cacheKey, { response: res, fetchedAt: Date.now() });
      }

      const payload = res.data;
      const nextUnavailable =
        payload.metadata?.unavailableTimeRanges ?? DEFAULT_UNAVAILABLE_TIME_RANGES;
      unavailableTimeRangesRef.current = nextUnavailable;
      setUnavailableTimeRanges(nextUnavailable);
      const snapped = coerceDashboardTimeRange(effectiveRange, nextUnavailable);
      if (snapped !== effectiveRange) {
        setTimeRange(snapped);
      }

      if (
        payload.status === "not_connected" ||
        payload.status === "no_asset" ||
        payload.status === "error"
      ) {
        const copy = getDashboardTileStatusCopy(
          toDashboardTileKey(payload.tileId || tileId),
          payload.status,
          payload.message
        );
        setData(payload);
        setStatusBanner(copy);
        setPartialCopy(null);
        setError(copy.body);
        if (payload.status === "error") {
          setHasFailed(true);
        }
        return;
      }

      if (payload.status === "partial") {
        const copy = getDashboardTileStatusCopy(
          toDashboardTileKey(payload.tileId || tileId),
          payload.status,
          payload.message,
          payload.reason
        );
        setData(payload);
        setStatusBanner(null);
        setPartialCopy(copy);
        return;
      }

      setData(payload);
      setStatusBanner(null);
      setPartialCopy(null);
    } catch (err) {
      log.error("Failed to load next-gen dashboard", {
        propertyId,
        tileId,
        error: err,
      });
      const copy = getDashboardTileStatusCopy(
        toDashboardTileKey(tileId),
        "error"
      );
      setStatusBanner(copy);
      setError(copy.body);
      setHasFailed(true);
    } finally {
      setLoading(false);
    }
  }, [propertyId, tileId, timeRange, compareMode, log]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (hasFailed && typeof onFailedLoad === "function") {
      onFailedLoad();
    }
  }, [hasFailed, onFailedLoad]);

  const kpis = useMemo<INextGenDashboardKpi[]>(() => data?.kpis ?? [], [data]);

  const chartsForRender = useMemo<INextGenDashboardChart[]>(() => {
    return data?.charts ?? [];
  }, [data]);

  // EV follows the approved sheet 03 (4019), at mid-fi (UX-05): page-level filter bar, KPI grid and
  // one card per chart. Other tiles keep the single-card layout until their sheets are approved.
  const useCardLayout = tileId === "ev";
  // Alan, 9/30: EV's Exception & Alert panel stays out of this phase (P2 on the low-fi sheet).
  const showExceptions = tileId !== "ev";

  // A canned Ask-bar question sets up the chart through the page's own range / compare controls.
  // The pick is kept here, keyed by chart title, because applying a preset refetches and the
  // cards remount behind the loading state.
  const [selectedQuestions, setSelectedQuestions] = useState<Record<string, string>>({});
  const handleQuestionSelect = useCallback(
    (chartTitle: string, question: INextGenDashboardQuestion) => {
      setSelectedQuestions((current) => ({ ...current, [chartTitle]: question.text }));
      if (question.timeRange) {
        setTimeRange(question.timeRange);
      }
      if (question.compareMode) {
        setCompareMode(question.compareMode);
      }
    },
    []
  );

  const renderChartBody = (chart: INextGenDashboardChart) => {
    const emptyBody = (
      <DashboardStatusBanner
        tone="neutral"
        body={chart.emptyMessage ?? "Chart data is not available for this tile yet."}
      />
    );
    if (chart.type === "heatmap") {
      // The heatmap draws its own faded grid + overlay when it has no cells.
      return <NextGenHeatmap chart={chart} />;
    }
    return chart.series?.length ? <NextGenChart chart={chart} /> : emptyBody;
  };

  const controls = (
    <NextGenChartControls
      timeRange={timeRange}
      unavailableTimeRanges={unavailableTimeRanges}
      compareMode={compareMode}
      onTimeRangeChange={setTimeRange}
      onCompareModeChange={setCompareMode}
      variant={useCardLayout ? "midfi" : "default"}
    />
  );

  /** Loading / not-connected / error state in place of the charts; null when charts should render. */
  const renderBlockingState = () => {
    if (loading) {
      return (
        <div className="flex h-[320px] items-center justify-center">
          <ClipLoader color="#f97316" size={40} />
        </div>
      );
    }
    if (statusBanner) {
      return (
        <DashboardStatusBanner
          tone="neutral"
          size="lg"
          align="center"
          heading={statusBanner.heading}
          body={statusBanner.body}
          cta={{
            label: statusBanner.ctaLabel,
            variant: "solid",
            onClick: () => {
              if (connect) {
                connect.handleCta(statusBanner);
                return;
              }
              const exploreSlug = pageConfigBySlug("explore")?.slug || "explore";
              navigate(exploreSurveyIntentPath(exploreSlug, statusBanner.exploreDomain));
            },
          }}
        />
      );
    }
    if (error) {
      return <DashboardStatusBanner tone="error" body={error} />;
    }
    return null;
  };

  const partialBanner = partialCopy ? (
    <DashboardStatusBanner
      tone="warning"
      heading={partialCopy.heading}
      body={partialCopy.body}
      cta={
        hasDashboardConnectCta(partialCopy.ctaAction)
          ? {
              label: partialCopy.ctaLabel,
              variant: "link",
              onClick: () => connect?.handleCta(partialCopy),
            }
          : undefined
      }
    />
  ) : null;

  const noChartsBanner = (
    <DashboardStatusBanner
      tone="neutral"
      size="lg"
      body="Chart data is not available for this tile yet."
    />
  );

  const renderCardLayout = () => {
    const blocking = renderBlockingState();
    if (blocking) {
      return (
        <div className="rounded-xl border border-mf-border bg-mf-card p-6 shadow-mf-card">{blocking}</div>
      );
    }
    if (!chartsForRender.length) {
      return noChartsBanner;
    }
    const [lead, ...rest] = chartsForRender;
    return (
      <div className="space-y-4">
        {partialBanner}
        <ChartCard
          chart={lead}
          onQuestionSelect={(question) => handleQuestionSelect(lead.title, question)}
          selectedQuestion={selectedQuestions[lead.title]}
        >
          {renderChartBody(lead)}
        </ChartCard>
        {rest.length ? (
          <div className="grid gap-4 lg:grid-cols-2">
            {rest.map((chart, index) => (
              <ChartCard
                key={`${chart.title}-${index}`}
                chart={chart}
                onQuestionSelect={(question) => handleQuestionSelect(chart.title, question)}
                selectedQuestion={selectedQuestions[chart.title]}
              >
                {renderChartBody(chart)}
              </ChartCard>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="mx-auto w-full max-w-[1600px] px-4 py-6 lg:px-6">
      {showExceptions && data?.metadata?.exceptions?.length ? (
        <div className="mb-6">
          <DashboardExceptionPanel exceptions={data.metadata.exceptions} />
        </div>
      ) : null}

      {useCardLayout ? (
        <div className="mb-3.5">
          <DetailFilterBar
            property={<FilterPropertySelect />}
            assetsLabel="All chargers"
            assetsHint="Charger-level filtering isn't available yet"
            basis={data?.metadata?.basisLabel}
          >
            {controls}
          </DetailFilterBar>
        </div>
      ) : null}

      {data?.metadata?.dataStatus?.length ? (
        <div className="mb-4">
          <DataStatusStrip
            sources={data.metadata.dataStatus}
            badge={data.metadata.dataStatusBadge}
            note={data.metadata.dataStatusNote}
          />
        </div>
      ) : null}

      {data?.metadata?.coverage ? (
        <div className="mb-3 -mt-2">
          <CoverageDisclosure
            coverage={data.metadata.coverage}
            propertyId={propertyId}
            companyId={companyId}
            align="left"
          />
        </div>
      ) : null}

      <div className={useCardLayout ? "mb-4" : "mb-6"}>
        <KpiStrip kpis={kpis} compareMode={compareMode} layout={useCardLayout ? "grid" : "carousel"} />
      </div>

      {useCardLayout ? renderCardLayout() : (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm uppercase tracking-wide text-slate-500">
              {data?.tileId ?? "Building"}
            </div>
            <div className="text-xl font-semibold text-slate-900">
              {chartsForRender[0]?.title ?? "Performance"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {data?.metadata?.fuelScope ? (
              <DashboardStatusBadge
                tone="neutral"
                label={data.metadata.fuelScope}
              />
            ) : null}
            {data?.metadata?.isStale ? (
              <DashboardStatusBadge tone="warning" label="Data may be stale" />
            ) : null}
          </div>
        </div>

        <div className="mt-4">{controls}</div>

        <div className="mt-6">
          {renderBlockingState() ?? (
            <div>
              {partialBanner ? <div className="mb-4">{partialBanner}</div> : null}
              {chartsForRender.length ? (
                <div className="space-y-6">
                  {chartsForRender.map((chart, index) => (
                    <div key={`${chart.title}-${index}`}>
                      {chartsForRender.length > 1 ? (
                        <div className="mb-2 text-sm font-medium text-slate-700">
                          {chart.title}
                        </div>
                      ) : null}
                      {renderChartBody(chart)}
                    </div>
                  ))}
                </div>
              ) : (
                noChartsBanner
              )}
            </div>
          )}
        </div>
      </div>
      )}

      {data?.metadata?.warnings?.length ? (
        <div className="mt-4">
          <DashboardStatusBanner
            tone="warning"
            heading="Notes"
            items={data.metadata.warnings}
          />
        </div>
      ) : null}

      {data?.metadata?.isStale ? (
        <div className="mt-4">
          <DashboardStatusBanner
            tone="warning"
            heading="Data freshness"
            body={`Latest data is ${
              data.metadata.stalenessHours ?? "unknown"
            } hours old.${
              data.metadata.disconnectionHoursThreshold
                ? ` Disconnection threshold is ${data.metadata.disconnectionHoursThreshold} hours.`
                : ""
            }`}
          />
        </div>
      ) : null}
    </div>
  );
};
